
    import { Vue, Component, Prop, Watch } from 'vue-property-decorator'

    @Component
    export default class DocumentPreview extends Vue {
        @Prop({ default: null }) readonly file!: File | null
        @Prop({ type: Boolean, default: false }) readonly openModal!: boolean
        @Prop({ type: String, default: 'view=FitH' }) readonly pdfParams!: string

        previewUrl: string | null = null
        documentUrl: string | null = null
        previewType: 'image' | 'pdf' | null = null
        previewError: string | null = null
        loading = false
        showModal = false
        isFullscreen = false

        get fileName(): string {
            return this.file?.name || ''
        }

        get pdfUrl(): string {
            return `${this.documentUrl}#${this.pdfParams}`
        }

        @Watch('file')
        async onFileChange(newFile: File | null): Promise<void> {
            if (!newFile) {
                this.clearPreview()
                return
            }

            this.loading = true

            try {
                await this.generatePreview(newFile)
            } catch (error) {
                console.error('Error generating preview:', error)
                this.previewError = this.$t('productUploadDocumentPreviewError').toString()
            } finally {
                this.loading = false
            }
        }

        @Watch('openModal')
        onOpenModalChange(shouldOpen: boolean): void {
            if (shouldOpen && this.file) {
                this.showModal = true
                this.$emit('modal-opened')
            }
        }

        async generatePreview(file: File): Promise<void> {
            const fileType = file.type

            if (fileType === 'application/pdf') {
                await this.renderPdfPreview(file)
            } else if (fileType.startsWith('image/')) {
                await this.renderImagePreview(file)
            } else {
                this.previewError = this.$t('productUploadDocumentPreviewUnsupported').toString()
            }
        }

        async renderImagePreview(file: File): Promise<void> {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()

                reader.onload = (e: ProgressEvent<FileReader>) => {
                    this.previewUrl = e.target?.result as string
                    this.previewType = 'image'
                    this.previewError = null
                    resolve()
                }

                reader.onerror = () => {
                    this.previewError = this.$t('productUploadDocumentPreviewError').toString()
                    reject(new Error('Failed to read image file'))
                }

                reader.readAsDataURL(file)
            })
        }

        renderPdfPreview(file: File): void {
            try {
                if (this.documentUrl && this.documentUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(this.documentUrl)
                }

                this.documentUrl = URL.createObjectURL(file)
                this.previewType = 'pdf'
                this.previewError = null
            } catch (error) {
                console.error('Error loading PDF:', error)
                this.previewError = this.$t('productUploadDocumentPreviewError').toString()
                throw error
            }
        }

        closeModal(): void {
            this.showModal = false
            this.isFullscreen = false
        }

        toggleFullscreen(): void {
            this.isFullscreen = !this.isFullscreen
            this.focusPdfViewer()
        }

        /** Focus the iframe so arrow keys work without needing to click on the PDF first. */
        focusPdfViewer(): void {
            this.$nextTick(() => {
                const iframe = this.$refs.pdfIframe as HTMLIFrameElement | undefined
                if (!iframe) return

                iframe.focus()

                // Chromium routes the PDF plugin's key handling through the nested browsing
                // context, which the element-level focus above does not always reach.
                const frameWindow = iframe.contentWindow
                if (frameWindow) {
                    frameWindow.focus()
                }
            })
        }

        clearPreview(): void {
            if (this.documentUrl && this.documentUrl.startsWith('blob:')) {
                URL.revokeObjectURL(this.documentUrl)
            }

            this.previewUrl = null
            this.documentUrl = null
            this.previewType = null
            this.previewError = null
            this.loading = false
            this.showModal = false
            this.isFullscreen = false
        }

        beforeDestroy(): void {
            this.clearPreview()
        }
    }
